import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { MetricsRange, PrometheusResponse, TrafficQuotaResponse } from '@partiri-cloud/types';

/**
 * @class ServiceMetricsApi
 * @description Metrics related to a service
 */
export class ServiceMetricsApi {
  endpoint = '/metrics'
  constructor(private http: HttpClient) { }

  private buildParams(deployTag: string, range?: MetricsRange): HttpParams {
    const params = new HttpParams().set('deployTag', deployTag);
    if (!range) return params;
    return params
      .set('start', String(range.start))
      .set('end', String(range.end));
  }

  /**
   * Read CPU usage for service
   * @param service
   * @param range optional time range
   * @returns
   */
  public readCpu(service: string, deployTag: string, range?: MetricsRange): Observable<PrometheusResponse> {
    return this.http.get<PrometheusResponse>(`${this.endpoint}/cpu/${service}`, { params: this.buildParams(deployTag, range) })
  }

  /**
   * Read RAM usage for service
   * @param service
   * @param deployTag deployment tag for pod filtering
   * @param range optional time range
   * @returns
   */
  public readMemory(service: string, deployTag: string, range?: MetricsRange): Observable<PrometheusResponse> {
    return this.http.get<PrometheusResponse>(`${this.endpoint}/memory/${service}`, { params: this.buildParams(deployTag, range) })
  }

  /**
   * Read Network usage for service
   * @param service
   * @param deployTag deployment tag for pod filtering
   * @param range optional time range
   * @returns
   */
  public readNetwork(service: string, deployTag: string, range?: MetricsRange): Observable<{ download: PrometheusResponse; upload: PrometheusResponse }> {
    return this.http.get<{ download: PrometheusResponse; upload: PrometheusResponse }>(`${this.endpoint}/network/${service}`, { params: this.buildParams(deployTag, range) })
  }

  /**
   * Read Disk usage for service
   * @param service
   * @param deployTag deployment tag for pod filtering
   * @param range optional time range
   * @returns
   */
  public readDisk(service: string, deployTag: string, range?: MetricsRange): Observable<PrometheusResponse> {
    return this.http.get<PrometheusResponse>(`${this.endpoint}/disk/${service}`, { params: this.buildParams(deployTag, range) })
  }

  /**
   * Read monthly traffic quota status for service
   * @param service
   * @returns
   */
  public readTrafficQuota(service: string): Observable<TrafficQuotaResponse> {
    return this.http.get<TrafficQuotaResponse>(`${this.endpoint}/traffic-quota/${service}`)
  }
}
